import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { useBoxConsumableActions } from "@modules/boxConsumable/hooks/useBoxConsumable"
import { creditPurchaseYupValidation } from "@modules/boxPurchases/formik/boxPurchasesFormik"
import { word } from "@modules/core/utils/i18n"
import { InputNumberCursor } from "@modules/formik/components/inputNumberCursor"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { useFormik } from "formik"
import { useCallback, useImperativeHandle, useMemo, useState } from "react"
import { useTheme } from "styled-components"
import boxOfferTicketIcon from "@images/illustrations/boxOfferTicketIcon.svg"

export type BoxPurchasesCreditModalRef = {
  open: () => void
}

export type BoxPurchasesCreditModalProps = {
  memberId: string
  consumableDetails: BoxConsumableDetailsEntity | null
  ref: React.Ref<BoxPurchasesCreditModalRef>
}

export function BoxPurchasesCreditModal({ memberId, consumableDetails, ref }: BoxPurchasesCreditModalProps) {
  const { creditPurchase, isCreditPurchasePending } = useBoxConsumableActions(memberId)
  const [showModal, setShowModal] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const theme = useTheme()
  const toast = useToast()
  const formik = useFormik({
    initialValues: {
      newCredits: 0,
    },
    validationSchema: creditPurchaseYupValidation(consumableDetails?.count ?? null),
    onSubmit: async () => {
      if (!consumableDetails) return
      setIsSubmit(false)
      await creditPurchase({ purchaseId: consumableDetails.id, newCredits: formik.values.newCredits })
        .then(() => {
          if (formik.values.newCredits > 0) {
            toast.show({
              severity: "success",
              summary: word("box.purchases.modal.credit.toast.success"),
            })
          } else {
            toast.show({
              severity: "success",
              summary: word("box.purchases.modal.credit.toast.decreaseSuccess"),
            })
          }
          onHide()
        })
        .catch(() => {
          toast.show({
            severity: "error",
            summary: word("box.purchases.modal.credit.toast.error"),
          })
        })
        .finally(() => {
          setIsSubmit(false)
        })
    },
  })

  const onHide = useCallback(() => {
    formik.resetForm()
    setShowModal(false)
  }, [formik])

  const onSubmit = async () => {
    setIsSubmit(true)
    formik.handleSubmit()
  }

  const colorText = useMemo(() => {
    if (formik.values.newCredits < 0) {
      return theme.error
    } else if (formik.values.newCredits > 0) {
      return theme.sucess
    }
    return theme.mainWhite
  }, [formik.values.newCredits])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  if (!consumableDetails) return null

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "512px" }}>
      {/* HEADER */}
      <div className="flex flex-column gap-5">
        <div className="flex flex-column flex-wrap pt-5 gap-2 justify-content-center align-content-center text-center">
          <div className="flex w-full justify-content-center align-content-center">
            <img src={boxOfferTicketIcon} alt="boxOfferTicketIcon" />
          </div>
          <HUText fontStyle="TL" color={theme.mainWhite}>
            {word("box.purchases.modal.credit.header.title")}
          </HUText>
        </div>
        {/* CONTENT */}
        <HUText
          fontStyle="BM"
          className="flex  justify-content-center align-content-center text-center"
          color={theme.mainWhite}
        >
          {word("box.purchases.modal.credit.header.subtitle")}
        </HUText>
        <div className="flex flex-column">
          <div className="flex gap-3 w-full justify-content-center align-content-center">
            <InputNumberCursor
              name="newCredits"
              isRequiredInput={true}
              isInvalid={isFormFieldInValid("newCredits", formik)}
              setFieldValue={formik.setFieldValue}
              initialValue={formik.values.newCredits}
            />
            <HUText fontStyle="TS" color={theme.mainWhite} className="flex align-items-center">
              {word("box.purchases.modal.credit.content.inputTexte", { count: formik.values.newCredits })}
            </HUText>
          </div>
          <div className="flex pl-2">
            {getFormErrorMessage("newCredits", formik, {
              errorMessage: formik.errors.newCredits,
              showAlways: true,
            })}
          </div>
        </div>
        {consumableDetails.count !== null && (
          <div className="flex flex-column ">
            <div className="flex w-full justify-content-center align-content-center text-center gap-4">
              <div className="flex flex-column gap-1">
                <HUText fontStyle="LS" color={theme.neutralColor700} className="mb-2">
                  {word("box.purchases.modal.credit.content.now")}
                </HUText>
                <HUText fontStyle="TS" color={theme.mainWhite}>
                  {consumableDetails.count}
                </HUText>
                <HUText fontStyle="TS" color={theme.mainWhite}>
                  {word("box.purchases.modal.credit.content.inputTexte", { count: consumableDetails.count })}
                </HUText>
              </div>
              <div className="flex align-items-end">
                <ArrowDrawIcon color={theme.neutralColor700} />
              </div>
              <div className="flex flex-column gap-1">
                <HUText fontStyle="LS" color={theme.neutralColor700} className="mb-2">
                  {word("box.purchases.modal.credit.content.then")}
                </HUText>
                <HUText fontStyle="TS" color={colorText}>
                  {consumableDetails.count + formik.values.newCredits}
                </HUText>
                <HUText fontStyle="TS" color={colorText}>
                  {word("box.purchases.modal.credit.content.inputTexte", {
                    count: consumableDetails.count + formik.values.newCredits,
                  })}
                </HUText>
              </div>
            </div>
          </div>
        )}
        {/* BUTTON */}
        <div className="mt-5 flex flex-row gap-3 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={() => onHide()}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.check")}
            onClick={async () => await onSubmit()}
            disabled={
              isSubmit ||
              formik.values.newCredits === 0 ||
              isCreditPurchasePending ||
              (consumableDetails.count !== null && formik.values.newCredits < -consumableDetails.count)
            }
            className="w-full"
          />
        </div>
      </div>
    </HUDialog>
  )
}
